// ============================================================================
// Time & Labor — the account manager's side.
//
// ⛔ NO MONEY SHAPE EXISTS IN THIS FILE. There is no rate, wage, amount, cost
// or margin key anywhere in these types, and a test asserts it. A screen cannot
// leak what it was never given.
//
// A night manager is a POSITION filled by a person, never a fact about the
// person (Ben, 2026-09-21). The position carries the market and names the
// manager it reports to; the person is an assignment to it.
// ============================================================================

/** A night-manager position and whoever currently fills it. */
export interface NightManagerRow {
  position_id: string;
  title: string;
  market_region: string | null;
  /** The employee filling it, if anyone. */
  filled_by_eid: string | null;
  filled_by_name: string | null;
  /** The punch-clock worker behind that employee, if they have one. */
  worker_id: string | null;
  /** Their own language, so the joining message is written in it. */
  worker_language: WorkerLang;
  reports_to_eid: string | null;
  /** The AM's setting: false = Night only, true = Night + buildings (item #997). */
  nm_building_checkins: boolean;
  /** Are they on OUR clock yet, or still on the old system? */
  has_pin: boolean;
  nights: number;
  hours: number;
  buildings: number;
  notes: string | null;
}

/** One punch, as a record of where somebody was. */
export interface PunchPoint {
  punch_event_id: string;
  event_type: 'in' | 'out';
  event_at: string;
  facility_id: string | null;
  place_display: string | null;
  capture_mode: string | null;
  latitude: number | null;
  longitude: number | null;
  gps_accuracy_m: number | null;
  distance_from_site_m: number | null;
  geofence_result: string | null;
  /** Set when this punch replaced an earlier one. */
  supersedes_event_id: string | null;
  void_reason: string | null;
  created_by_eid: string | null;
}

/**
 * One night, as a ROUTE.
 *
 * ⛔ A night manager clocks out somewhere other than where they clocked in —
 * William did it on all three of his nights. Naming only the in-building would
 * report "Gateway Endo, 7.12 hours" for a night that covered two buildings 14
 * miles apart, so both ends are carried.
 */
export interface NightRow {
  segment_id: string;
  worker_id: string;
  shift_date: string;
  started_at: string | null;
  ended_at: string | null;
  hours: number;
  /** The night's paid minutes, unrounded — what the Building time panel adds up to. */
  paid_minutes: number;
  segment_status: string;
  in_place: string | null;
  out_place: string | null;
  /** True when either end was typed by a person rather than clocked. */
  was_corrected: boolean;
  /** Metres from the building on the furthest-out punch of the night. */
  furthest_m: number | null;
  open_exception: string | null;
  /** The id of that open item, so the pinned banner can offer the door that
   *  settles it rather than only naming the problem. */
  open_exception_id: string | null;
  punches: PunchPoint[];
  /** The worker's clock: the time zone of the market the building is in (markets.timezone). */
  site_tz: string;
}

/** A row on the hours tab — one shift, scoped to the buildings you hold. */
export interface HoursRow {
  segment_id: string;
  worker_id: string;
  worker_name: string;
  facility_id: string | null;
  place_display: string | null;
  shift_date: string;
  started_at: string | null;
  ended_at: string | null;
  hours: number;
  segment_status: string;
  open_exception: string | null;
  /** Is this shift at one of MY buildings? Drives the "show everyone" label. */
  mine: boolean;
  /** The worker's clock: the time zone of the market the building is in (markets.timezone). */
  site_tz: string;
}

/** The language the joining message is written in — theirs, off their record. */
export type WorkerLang = 'es' | 'en';

/** One outside reader's use of an hours page. */
export interface ClientViewRow {
  facility_id: string | null;
  link_id: string;
  label: string | null;
  link_scope: string;
  provider_id: string | null;
  link_is_live: boolean;
  expires_at: string | null;
  revoked_at: string | null;
  last_opened_at: string | null;
  opens_30d: number;
  opens_total: number;
  usage_state: 'never opened' | 'using it' | 'slowing down' | 'stopped looking';
  /** Filled in by the screen from the facility list. */
  place_display?: string | null;
}

/** A building of mine that has no hours link at all — the most useful row. */
export interface ClientViewGap {
  facility_id: string;
  place_display: string;
}

/**
 * How far off a punch has to be before the screen marks it without being asked.
 * Ben, 2026-09-21: detail lives behind a click, "but a single marker on the row
 * when a punch is more than, say, 300 m out".
 */
export const FAR_PUNCH_M = 300;
